import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Component, OnInit, Input, EventEmitter, Output, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertService } from '@app/_services';
import * as moment from 'moment';
import { PrimeNGConfig } from 'primeng/api';
import { OccMatrixService } from '../occ_matrix.service';
import { OccMatrix } from '../occ_matrix';
import { OccMapService } from '../../occ-map';
import { RateSeasonService } from '../../rate-season/rate-season.service';

@Component({
    selector: 'app-occMatrix',
    templateUrl: './occ_matrix.component.html',
    styleUrls: ['./occ_matrix.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class OccMatrixComponent implements OnInit {

	constructor(
		private primengConfig: PrimeNGConfig,
		private occMatrixService: OccMatrixService,
		private rateSeasonService: RateSeasonService,
		private occMapService: OccMapService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private formBuilder: UntypedFormBuilder,
		@Inject(MAT_DIALOG_DATA) data: any) {
		this.formTitle = data.title,
			this.occMatrix = data.occMatrix,
			this.statuses = occMatrixService.statuses;
	}

	@Input() occMatrix: OccMatrix;
	@Output() doAdd = new EventEmitter<any>();
	@Output() doUpdate = new EventEmitter<any>();
	@Output() doDelete = new EventEmitter<any>();

	// data
	occMatrixId: number;

	// forms
	form1: UntypedFormGroup;
	formLoading = false;
	formAlert: String;
	formTitle: String;

	statuses: any;

	occMapList: any = [];

	seasonCodeList: { label: string; value: string }[] = [];

	validationMessages = {
		'occMatrixId': [],
		occMapId: [
			{ type: 'required', message: 'Occ Map is required.' }
		],
		 daysToArrivalTo: [
			{ type: 'required', message: 'Days To Arrival (To) is required' },
			{ type: 'min', message: 'Value must be >= 0' },
			{ type: 'pattern', message: 'Only numeric values allowed' }
		],
		daysToArrivalFrom: [
			{ type: 'required', message: 'Days To Arrival (From) is required' },
			{ type: 'min', message: 'Value must be >= 0' },
			{ type: 'pattern', message: 'Only numeric values allowed' }
		],
		expNominalOccToDatePct: [
			{ type: 'required', message: 'Expected Nominal Occupancy % is required' },
			{ type: 'min', message: 'Value must be >= 0' },
			{ type: 'max', message: 'Value must be <= 100' },
			{ type: 'pattern', message: 'Only whole numbers (0–100) allowed.' }
		],
		seasonCode: [
			{ type: 'required', message: 'Season Code is required' },
			{ type: 'minlength', message: 'Minimum 3 characters required' },
			{ type: 'pattern', message: 'Only letters and numbers allowed' }
		]
	};

	ngOnInit() {
		this.primengConfig.ripple = true;
		this.formBuild();
		this.findOccMapList();
		// if we were passed the whole occMatrix object
		if (this.occMatrix) {
			this.occMatrixId = this.occMatrix.occMatrixId;
			this.form1.patchValue({
				...this.occMatrix,
				expNominalOccToDatePct: this.toPercent(this.occMatrix.expNominalOccToDatePct),
				shortTermGapMultiplierPCT: this.toPercent(this.occMatrix.shortTermGapMultiplierPCT),
				longTermGapMultiplierPCT: this.toPercent(this.occMatrix.longTermGapMultiplierPCT),
				longTermGapEmptyMultiplierPCT: this.toPercent(this.occMatrix.longTermGapEmptyMultiplierPCT),
				connectingFrontOrBackMultiplierPCT: this.toPercent(this.occMatrix.connectingFrontOrBackMultiplierPCT),
				connectingFrontAndBackMultiplierPCT: this.toPercent(this.occMatrix.connectingFrontAndBackMultiplierPCT),

    			seasonCode: null
			});
		}
		this.getSeasonCodes();
		this.form1.setValidators(this.daysRangeValidator);
	}

	private toPercent(value: number | null | undefined): number | '' {
		// Round to avoid floating-point noise (e.g. 0.95 * 100 = 95.00000000000001)
		return value != null ? Math.round(value * 10000) / 100 : '';
	}

	findOccMapList()
	{
		try {
			this.occMapService.findAll()
				.subscribe(
					(res: any) => {
						this.occMapList = res.data.occMaps;
					},
					(err: any) => {
						console.log('occMatrix-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	onFormDelete() {
		console.log('onRowDelete: ' + JSON.stringify(event));
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + this.occMatrixId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formDelete()
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}

	formAdd() {
		try {
			// stop here if form is invalid
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			const formValue = { ...this.form1.value };
			this.convertPercentToDecimal(formValue);

			// Extract value from dropdown object
			if (formValue.seasonCode && typeof formValue.seasonCode === 'object') {
				formValue.seasonCode = formValue.seasonCode.value;
			}
			console.log('property-add post: ' + JSON.stringify(this.form1.value));
			this.formLoading = true;
			this.occMatrixService.add(formValue)
				.subscribe(
					(res: any) => {
						console.log('property-add SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.occMatrix = res.data.occMatrix;
						this.doAdd.emit(this.occMatrix);
					},
					(err: any) => {
						console.log('occMatrix-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	formUpdate() {
		console.log('occMatrix: ' + JSON.stringify(this.form1.value));
		try {
			// stop here if form is invalid
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			
			const formValue = { ...this.form1.value };
			this.convertPercentToDecimal(formValue);
			
			// Extract just the `value` from the seasonCode object
			if (formValue.seasonCode && typeof formValue.seasonCode === 'object') {
				formValue.seasonCode = formValue.seasonCode.value;
			}

			this.formLoading = true;

			this.occMatrixService.update(formValue)
				.subscribe(
					(res: any) => {
						console.log('property-update SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.occMatrix = res.data.occMatrix;
						this.doUpdate.emit(this.occMatrix);
					},
					(err: any) => {
						console.log('property-update FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	private daysRangeValidator(group: UntypedFormGroup) {
		const from = group.get('daysToArrivalFrom')?.value;
		const to = group.get('daysToArrivalTo')?.value;

		if (from != null && to != null && from < to) {
			return { rangeInvalid: true };
		}
		return null;
	}

	private convertPercentToDecimal(formValue: any) {

		const percentFields = [
			'expNominalOccToDatePct',
			'shortTermGapMultiplierPCT',
			'longTermGapMultiplierPCT',
			'longTermGapEmptyMultiplierPCT',
			'connectingFrontOrBackMultiplierPCT',
			'connectingFrontAndBackMultiplierPCT'
		];

		percentFields.forEach(field => {
			if (formValue[field] !== null && formValue[field] !== '') {
				formValue[field] = formValue[field] / 100;
			}
		});
	}

	formDelete() {
		try {
			this.occMatrixService.delete(this.occMatrix.occMatrixId)
				.subscribe(
					(res: any) => {
						console.log('occMatrix-delete SUCCESS: ' + JSON.stringify(res));
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.doDelete.emit(this.occMatrix);
					},
					(err: any) => {
						console.log('occMatrix-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			this.alertService.error(error);
		}
	}

	getSeasonCodes()
	{
		this.rateSeasonService.getSeasonCodes().subscribe(res => {
			const codes = res.data.seasonCodes;
			this.seasonCodeList = codes.map(code => ({ label: code, value: code }));

			// If editing and form already has a seasonCode, patch it again after list is loaded
			const selectedCode = this.occMatrix?.seasonCode;  // Read from original object
			if (selectedCode) {
				const matched = this.seasonCodeList.find(option => option.value === selectedCode);
				if (matched) {
					this.form1.patchValue({ seasonCode: matched });
				}
			}
		});
	}

	formBuild() {
		this.form1 = this.formBuilder.group({
			occMatrixId: [this.occMatrixId],
			occMapId: [null, Validators.required],
			daysToArrivalTo: ['', [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]+$')
			]],
			daysToArrivalFrom: ['', [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]+$')
			]],
			expNominalOccToDatePct: ['', [
				Validators.required,
				Validators.min(0),
				Validators.max(100),
				Validators.pattern('^[0-9]+$') // Only whole numbers
			]],
			seasonCode: ['', Validators.required],
			// ===== NEW FIELDS =====
			// minStayExpectedNTS is a decimal (e.g. 3.5417) — no integer-only pattern
			minStayExpectedNTS: ['', [
				Validators.required,
				Validators.min(0)
			]],

			// Gap/connecting multipliers can be negative or decimal — min/max are sufficient
			shortTermGapMultiplierPCT: ['', [
				Validators.min(-100),
				Validators.max(100)
			]],

			shortTermGapMinNumNightsRevNTS: ['', [
				Validators.min(0)
			]],

			longTermGapMultiplierPCT: ['', [
				Validators.min(-100),
				Validators.max(100)
			]],

			longTermGapEmptyMultiplierPCT: ['', [
				Validators.min(-100),
				Validators.max(100)
			]],

			connectingFrontOrBackMultiplierPCT: ['', [
				Validators.min(-100),
				Validators.max(100)
			]],

			connectingFrontAndBackMultiplierPCT: ['', [
				Validators.min(-100),
				Validators.max(100)
			]]
		});
	}
}
